// Färdiga listor över service som bör finnas inom gångavstånd, per målgrupp.
//
// Riktavstånden kommer från forskning och planeringspraxis (RMIT Urban
// Liveability Checklist, WHO/ISGlobal 300 m-regeln, Mölndals lekplatsprogram,
// Boverket). De är riktvärden, inte svenska krav. Avstånd är gångavstånd längs
// gatunätet om inte `crowFlies` är satt (då fågelvägen, som i WHO-riktlinjen).
//
// `types` är platstyper i Google Places API (New), Table A. De söks med
// Place.searchNearby (includedTypes) — INTE med den gamla PlacesService
// nearbySearch, som tyst ignorerar okända typer och returnerar vad som helst.
//
// `maxMeters` styr bedömningen (finns / finns men längre bort / saknas) och
// sökradien (maxMeters × RADIUS_FACTOR, fågelvägen). `distanceLabel` är texten
// som visas för användaren när riktvärdet är ett spann eller har förbehåll.

export const RADIUS_FACTOR = 1.5;
export const MAX_SEARCH_RADIUS = 2500;

export const searchRadiusFor = (need) =>
  Math.min(MAX_SEARCH_RADIUS, Math.round(need.maxMeters * (need.crowFlies ? 1.2 : RADIUS_FACTOR)));

const GREEN_300 = {
  id: 'park',
  label: 'Park eller grönyta',
  types: ['park', 'city_park', 'garden'],
  maxMeters: 300,
  crowFlies: true,
  why: 'Återhämtning, rörelse, naturkontakt',
  source: 'WHO via ISGlobal, 3-30-300'
};

const GROCERY = {
  id: 'grocery',
  label: 'Livsmedelsbutik',
  types: ['supermarket', 'grocery_store', 'farmers_market'],
  maxMeters: 800,
  why: 'Små inköp till fots',
  source: 'RMIT'
};

const CLINIC = {
  id: 'clinic',
  label: 'Vårdcentral',
  types: ['medical_clinic', 'doctor'],
  maxMeters: 800,
  why: 'Låg tröskel till primärvård',
  source: 'RMIT'
};

const PHARMACY = {
  id: 'pharmacy',
  label: 'Apotek',
  types: ['pharmacy'],
  maxMeters: 800,
  why: 'Vardagsärenden utan bil',
  source: 'Planeringspraxis'
};

// Any public transport counts - a bus stop as well as a train or metro
// station (user decision 2026-10-24; bus and rail used to be two needs).
const TRANSIT_TYPES = ['bus_stop', 'tram_stop', 'train_station', 'subway_station', 'light_rail_station'];

const TRANSIT = {
  id: 'transit',
  label: 'Kollektivtrafik (buss, spårvagn, tåg eller tunnelbana)',
  types: TRANSIT_TYPES,
  maxMeters: 400,
  distanceLabel: '≤ 400 m (spårtrafik 800 m)',
  why: 'Aktiv resa och pendling utan bil',
  source: 'RMIT'
};

const PRESCHOOL = {
  id: 'preschool',
  label: 'Förskola',
  types: ['preschool'],
  maxMeters: 800,
  why: 'Lämning och hämtning till fots',
  source: 'RMIT'
};

const PRIMARY_SCHOOL = {
  id: 'primary_school',
  label: 'Grundskola (F–6)',
  types: ['primary_school'],
  maxMeters: 800,
  why: 'Barn kan gå eller cykla själva',
  source: 'RMIT'
};

const PLAYGROUND = {
  id: 'playground',
  label: 'Lekplats',
  types: ['playground', 'indoor_playground', 'skateboard_park'],
  maxMeters: 400,
  why: 'Daglig lek och rörelse',
  source: 'Mölndals lekplatsprogram'
};

export const TARGET_GROUP_PRESETS = [
  {
    id: 'core',
    label: 'Kärna för alla',
    intro:
      'Fyra saker återkommer för alla målgrupper: livsmedelsbutik, kollektivtrafik, grönyta inom 300 m och vårdcentral. En rimlig första kontroll av en plats.',
    needs: [GROCERY, TRANSIT, GREEN_300, CLINIC]
  },
  {
    id: 'children',
    label: 'Barn (0–12 år)',
    intro:
      'Målet är att barn själva ska kunna gå eller cykla till vardagens platser. För små barn räknas förälderns gångavstånd med vagn, så kortare avstånd än riktvärdena är en fördel.',
    needs: [
      PLAYGROUND,
      { ...GREEN_300, types: ['park', 'city_park', 'garden', 'woods', 'nature_preserve'], why: 'Fysisk aktivitet, återhämtning, naturkontakt' },
      PRESCHOOL,
      PRIMARY_SCHOOL,
      {
        id: 'sports',
        label: 'Spontanidrott, bollplan, simhall',
        types: ['athletic_field', 'sports_complex', 'swimming_pool', 'sports_club'],
        maxMeters: 1000,
        distanceLabel: 'ca 800 m – 1 km',
        why: 'Rörelse utan föreningskrav',
        source: 'Planeringspraxis'
      },
      {
        id: 'culture',
        label: 'Bibliotek eller fritidsgård',
        types: ['library', 'community_center'],
        maxMeters: 1000,
        distanceLabel: 'ca 1 km',
        why: 'Läsning, fritid, trygg vuxennärvaro',
        source: 'Planeringspraxis',
        gap: 'Fritidsgård och kulturskola saknas som egna typer i Google.'
      }
    ],
    gaps: 'Trygg skolväg, friyta på förskole- och skolgård samt fritidsgård går inte att mäta med Googles platstyper.'
  },
  {
    id: 'adults',
    label: 'Singlar och par utan barn',
    intro:
      'Hälsan hänger främst på vardagsmotion via gång och kollektivtrafik samt sociala mötesplatser som motverkar ensamhet. Blandade funktioner och täthet gör att fler går.',
    needs: [
      TRANSIT,
      GROCERY,
      {
        id: 'meeting',
        label: 'Kafé, restaurang eller torg',
        types: ['cafe', 'coffee_shop', 'restaurant', 'plaza'],
        maxMeters: 800,
        distanceLabel: 'ca 400–800 m',
        why: 'Spontana möten, minskad ensamhet',
        source: 'Planeringspraxis'
      },
      { ...GREEN_300, types: ['park', 'city_park', 'hiking_area'], why: 'Återhämtning, stressreduktion, motion' },
      {
        id: 'gym',
        label: 'Gym eller träningslokal',
        types: ['gym', 'fitness_center', 'yoga_studio'],
        maxMeters: 1000,
        distanceLabel: 'ca 800 m – 1 km',
        why: 'Träning i vardagen',
        source: 'Planeringspraxis'
      },
      CLINIC,
      {
        id: 'culture',
        label: 'Bibliotek, kultur eller föreningslokal',
        types: ['library', 'community_center', 'cultural_center'],
        maxMeters: 1000,
        distanceLabel: '≤ 800 m – 1 km',
        why: 'Gemenskap och engagemang',
        source: 'RMIT (community centre ≤ 800 m)'
      },
      PHARMACY
    ],
    gaps: 'Arbetsplatser och coworking (cykelavstånd 3–5 km) ligger utanför gångavstånd och kontrolleras inte. Paketombud saknas som egen typ i Google.'
  },
  {
    id: 'families',
    label: 'Barnfamiljer',
    intro:
      'Vardagens kedja räknas: förskola, skola, butik och kollektivtrafik ska nås i samma gångrunda. Går kedjan sönder väljer familjen bilen. Barnens egna behov gäller också.',
    needs: [
      { ...PRESCHOOL, why: 'Lämning till fots, gärna på vägen till kollektivtrafiken' },
      { ...PRIMARY_SCHOOL, why: 'Barnen kan gå själva, föräldrarna sparar tid' },
      { ...GROCERY, why: 'Inköp i samma runda som lämning' },
      TRANSIT,
      { ...PLAYGROUND, types: ['playground'], why: 'Daglig utevistelse med små barn' },
      {
        id: 'bigpark',
        label: 'Större park eller naturområde',
        types: ['park', 'city_park', 'picnic_ground', 'barbecue_area'],
        maxMeters: 400,
        distanceLabel: '≤ 400 m (yta ≥ 1,5 ha)',
        why: 'Helgaktiviteter, lek i natur',
        source: 'RMIT',
        gap: 'Parkens storlek kontrolleras inte.'
      },
      { ...CLINIC, label: 'Vårdcentral med BVC', why: 'Täta besök under småbarnsåren', gap: 'BVC ligger oftast under vårdcentralen i Google.' },
      { ...PHARMACY, why: 'Vardagsbehov vid sjukdom' },
      {
        id: 'sports',
        label: 'Idrottshall, simhall, föreningsliv',
        types: ['swimming_pool', 'sports_complex', 'sports_club', 'athletic_field'],
        maxMeters: 1500,
        distanceLabel: 'ca 1–2 km (cykelavstånd)',
        why: 'Barnens fritidsaktiviteter utan skjuts',
        source: 'Planeringspraxis'
      },
      {
        id: 'culture',
        label: 'Bibliotek eller öppen förskola',
        types: ['library', 'community_center'],
        maxMeters: 1000,
        distanceLabel: 'ca 1 km',
        why: 'Stöd och nätverk för föräldrar',
        source: 'Planeringspraxis',
        gap: 'Öppen förskola saknas som egen typ i Google.'
      }
    ]
  },
  {
    id: 'seniors',
    label: 'Äldre',
    intro:
      'Närheten avgör om man kan fortsätta leva självständigt. Räckvidden är ofta kortare, så avstånden bör ligga i underkant och stråken ha bänkar, toaletter och jämna ytor.',
    needs: [
      { ...GROCERY, types: ['supermarket', 'grocery_store'], distanceLabel: '≤ 400–800 m, helst i underkant', why: 'Självständiga inköp, daglig promenad' },
      { ...CLINIC, why: 'Tätare vårdkontakter, mindre beroende av färdtjänst' },
      { ...PHARMACY, why: 'Regelbundna uttag' },
      {
        id: 'transit',
        label: 'Tillgänglig hållplats',
        types: TRANSIT_TYPES,
        maxMeters: 400,
        distanceLabel: '≤ 400 m, med sittplats och väderskydd',
        why: 'Att kunna resa själv',
        source: 'RMIT, WHO'
      },
      { ...GREEN_300, label: 'Park eller grönyta med sittplatser', types: ['park', 'garden'], why: 'Promenader, balans, återhämtning', source: 'WHO via ISGlobal, WHO' },
      {
        id: 'meeting',
        label: 'Träffpunkt eller mötesplats',
        types: ['community_center', 'cafe'],
        maxMeters: 800,
        why: 'Motverkar ensamhet och isolering',
        source: 'RMIT (community centre), WHO',
        gap: 'Träffpunkt för äldre saknas som egen typ i Google; samlingslokal och kafé används.'
      },
      {
        id: 'toilet',
        label: 'Offentlig toalett',
        types: ['public_bathroom'],
        maxMeters: 800,
        distanceLabel: 'längs stråk och vid centrum (här ≤ 800 m)',
        why: 'Avgör om äldre vågar gå ut',
        source: 'WHO',
        gap: 'Ofta ofullständigt registrerat i Google.'
      },
      {
        id: 'selfcare',
        label: 'Tandvård, fotvård eller frisör',
        types: ['dental_clinic', 'foot_care', 'physiotherapist', 'hair_salon', 'barber_shop'],
        maxMeters: 1000,
        distanceLabel: 'ca 1 km',
        why: 'Egenvård och vardagsrutiner',
        source: 'Planeringspraxis'
      },
      {
        id: 'assembly',
        label: 'Bibliotek, kyrka eller samlingslokal',
        types: ['library', 'church', 'mosque', 'synagogue'],
        maxMeters: 1000,
        distanceLabel: 'ca 1 km',
        why: 'Social delaktighet',
        source: 'Planeringspraxis'
      }
    ],
    gaps: 'Bänkar (var 100–200 m längs stråk) och träffpunkter för äldre går inte att mäta med Googles platstyper.'
  }
];

export const formatMeters = (m) =>
  m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1).replace('.', ',')} km`;

export const targetLabel = (need) => need.distanceLabel || `≤ ${formatMeters(need.maxMeters)}`;
